import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, Facebook, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PROJECT_TYPES = [
  "Metal Building",
  "Custom Vehicle or RV Carport",
  "Fencing",
  "Custom Welding",
  "Workshop",
  "Storage Building",
  "Concrete Work / Driveway / Walkway",
  "Other",
];

const inputCls =
  "w-full bg-[#F5F7FA] border border-[#D1D5DB] rounded-none px-4 py-3.5 text-[#0A0E1A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#84FB00] focus:ring-1 focus:ring-[#84FB00] transition-colors font-sans";

export default function ContactForm() {
  const { content } = useContent();
  const contact = content?.contact || {};
  const phone = contact.phone || "(254) 833-7563";
  const phoneLink = contact.phone_link || "+12548337563";
  const email = contact.email || "lcbuilders87@yahoo.com";
  const area = contact.area || "Central Texas";
  const cmsTypes = Array.isArray(content?.sections?.project_types)
    ? content.sections.project_types.filter((t) => t && t.trim())
    : [];
  const projectTypes = cmsTypes.length ? cmsTypes : FALLBACK_PROJECT_TYPES;

  const [form, setForm] = useState({ name: "", phone: "", email: "", project_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please fill in name, phone, email, and project type.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/estimate`, form);
      const gotAutoReply = res.data?.autoreply_delivered;
      toast.success(
        gotAutoReply
          ? "Thanks — confirmation email on the way. Hondo will call within 24 hours."
          : res.data?.message || "Estimate request received.",
        { duration: 6000 }
      );
      setForm({ name: "", phone: "", email: "", project_type: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">LET'S TALK</div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
            Request a<br />
            <span className="text-[#4AB300]">Free Estimate</span>
          </h2>
          <p className="mt-6 text-[#374151] leading-relaxed text-lg">
            Tell Hondo what you're building. He'll get back to you with a candid, honest bid — usually within a day.
          </p>

          <div className="mt-12 space-y-6">
            <a href={`tel:${phoneLink}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <div className="w-12 h-12 bg-white border border-[#E5E7EB] flex items-center justify-center group-hover:bg-[#84FB00] group-hover:border-[#84FB00] transition-colors">
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-[#6B7280]">Phone</div>
                <div className="font-display text-xl tracking-wide text-[#0A0E1A] group-hover:text-[#4AB300] transition-colors">{phone}</div>
              </div>
            </a>

            <a href={`mailto:${email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <div className="w-12 h-12 bg-white border border-[#E5E7EB] flex items-center justify-center group-hover:bg-[#84FB00] group-hover:border-[#84FB00] transition-colors">
                <Mail size={20} />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-[#6B7280]">Email</div>
                <div className="font-display text-xl tracking-wide text-[#0A0E1A] group-hover:text-[#4AB300] transition-colors">{email}</div>
              </div>
            </a>

            <div className="flex items-center gap-4" data-testid="contact-area">
              <div className="w-12 h-12 bg-white border border-[#E5E7EB] flex items-center justify-center">
                <MapPin size={20} />
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-[#6B7280]">Service Area</div>
                <div className="font-display text-xl tracking-wide text-[#0A0E1A]">{area}</div>
                {(contact.address_street ||
                  contact.address_city ||
                  contact.address_state) && (
                  <div className="text-sm text-[#4B5563] mt-1" data-testid="contact-address">
                    {[
                      contact.address_street,
                      [
                        contact.address_city,
                        [contact.address_state, contact.address_postal]
                          .filter(Boolean)
                          .join(" "),
                      ]
                        .filter(Boolean)
                        .join(", "),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
              </div>
            </div>

            {contact.hours_display && contact.hours_display.trim() && (
              <div className="flex items-center gap-4" data-testid="contact-hours">
                <div className="w-12 h-12 bg-white border border-[#E5E7EB] flex items-center justify-center">
                  <Clock size={20} />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-[#6B7280]">Hours</div>
                  <div className="font-display text-xl tracking-wide text-[#0A0E1A]">
                    {contact.hours_display}
                  </div>
                </div>
              </div>
            )}

            {contact.facebook_url && contact.facebook_url.trim() && (
              <a
                href={contact.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-white border border-[#E5E7EB] flex items-center justify-center group-hover:bg-[#1877F2] group-hover:border-[#1877F2] transition-colors">
                  <Facebook size={20} />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-[#6B7280]">Facebook</div>
                  <div className="font-display text-xl tracking-wide text-[#0A0E1A] group-hover:text-[#1877F2] transition-colors">
                    Follow us
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>

        <form onSubmit={submit} data-testid="estimate-form" className="lg:col-span-7 bg-white border border-[#E5E7EB] p-8 md:p-10">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs tracking-widest uppercase text-[#4B5563] mb-2">Full Name *</label>
              <input data-testid="estimate-name" className={inputCls} type="text" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="John Smith" required />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-[#4B5563] mb-2">Phone *</label>
              <input data-testid="estimate-phone" className={inputCls} type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="(254) 555-0123" required />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs tracking-widest uppercase text-[#4B5563] mb-2">Email *</label>
              <input data-testid="estimate-email" className={inputCls} type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs tracking-widest uppercase text-[#4B5563] mb-2">Project Type *</label>
              <Select value={form.project_type} onValueChange={(v) => setField("project_type", v)}>
                <SelectTrigger data-testid="estimate-type" className="w-full bg-[#F5F7FA] border border-[#D1D5DB] rounded-none px-4 py-3.5 h-auto text-[#0A0E1A] focus:ring-1 focus:ring-[#84FB00] focus:border-[#84FB00] font-sans">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent className="bg-[#F5F7FA] border border-[#D1D5DB] rounded-none text-[#0A0E1A]">
                  {projectTypes.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`estimate-type-option-${t.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-none focus:bg-[#84FB00] focus:text-[#0A0E1A]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs tracking-widest uppercase text-[#4B5563] mb-2">Project Details</label>
              <textarea data-testid="estimate-message" className={`${inputCls} min-h-[140px] resize-y`} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Tell Hondo about your project — dimensions, materials, timing, budget range..." />
            </div>
          </div>

          <button type="submit" disabled={submitting} data-testid="estimate-submit" className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#84FB00] hover:bg-[#6EE500] disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-8 py-5 font-display tracking-widest uppercase text-[#0A0E1A] text-lg">
            {submitting ? (<><Loader2 size={18} className="animate-spin" /> Sending</>) : (<><Send size={18} /> Send Estimate Request</>)}
          </button>
          <p className="mt-4 text-xs text-[#6B7280] text-center">Free · No obligation · Usually replies within 24 hours</p>
        </form>
      </div>
    </section>
  );
}
